import { debounce } from '@theme/utilities';
import { Component } from '@theme/component';

// Accordion
class AccordionCustom extends Component {
  /** @returns {boolean} */
  #isMobileViewport() {
    return this.#getViewportWidth() < 768;
  }

  #getViewportWidth() {
    return window.innerWidth;
  }

  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #prevIsMobile = window.innerWidth < 768;

  /** @type {(() => void) & { cancel?: () => void } | null} */
  #handleResize = null;

  /** @type {Animation | null} In-flight open/close height animation. */
  #anim = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setDefaultOpenState();
    this.#setupAutoCloseDetails();
    this.addEventListener('keydown', this.#handleKeyDown);
    this.summary.addEventListener('click', this.handleClick);
    this.#handleResize = debounce(() => {
      const isMobile = window.innerWidth < 768;
      if (isMobile === this.#prevIsMobile) return;
      this.#prevIsMobile = isMobile;
      this.#setDefaultOpenState();
    }, 50);
    window.addEventListener('resize', this.#handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#handleKeyDown);
    // Use a direct query (the summary getter throws) so a missing summary
    // can't abort cleanup and leak the resize listener.
    this.querySelector('summary')?.removeEventListener('click', this.handleClick);
    window.removeEventListener('resize', this.#handleResize);
    this.#handleResize?.cancel?.();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = this.#isMobileViewport();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // Get all accordion items in the same parent
    const parent = this.closest('.accordion--single-open');

    if (parent) {
      event.preventDefault();

      const allAccordions = parent.querySelectorAll('accordion-custom');
      allAccordions.forEach(accordion => {
        if (accordion !== this) {
          accordion.close();
        }
      });
    }
    if( this.classList.contains('accordion-animated')) {
      event.preventDefault();
      const isOpening = this.details.open;
        if (isOpening) {
          this.close();
        } else {
          this.open();
        }
    }
  };

  open() {
    const content = this.details.querySelector('.details-content');
    if (!content) return;

    if (this.details.open) return;

    content.style.overflow = 'hidden';
    this.details.open = true;
    this.details.dispatchEvent(new Event('toggle', { bubbles: true }));

    const targetHeight = this.#getExpandedContentHeight(content);
    this.#animateHeight(content, ['0px', `${targetHeight}px`], () => {
      content.style.overflow = '';
    });
  }

  close() {
    const content = this.details.querySelector('.details-content');
    if (!content) return;

    if (!this.details.open) return;

    const startHeight = content.getBoundingClientRect().height;
    content.style.overflow = 'hidden';
    this.#animateHeight(content, [`${startHeight}px`, '0px'], () => {
      this.details.open = false;
      this.details.dispatchEvent(new Event('toggle', { bubbles: true }));
      content.style.overflow = '';
    });
  }

  /**
   * Animate content height via the native Web Animations API: the browser
   * interpolates it, so there is no per-frame JS tween (the old Anime.js one is
   * what stuttered on mobile). fill defaults to none, so the height snaps back to
   * the stylesheet value once done. Cancelling the stored animation makes rapid
   * open/close interruptions clean.
   * @param {HTMLElement} content
   * @param {[string, string]} heights - [from, to]
   * @param {() => void} onDone
   */
  #animateHeight(content, heights, onDone) {
    this.#anim?.cancel();
    this.#anim = content.animate(
      { height: heights },
      { duration: 200, easing: 'cubic-bezier(0.39, 0.575, 0.565, 1)' }
    );
    this.#anim.finished
      .then(() => {
        this.#anim = null;
        onDone();
      })
      .catch(() => {});
  }

  #getExpandedContentHeight(content) {
    return content.querySelector('.details-content__inner')?.getBoundingClientRect().height || content.scrollHeight;
  }

  /**
   * Sets up auto-close functionality for details elements (only once globally)
   */
  #setupAutoCloseDetails = () => {
    // Only set up the global listener once
    if (!AccordionCustom.#autoCloseListenerAdded) {
      AccordionCustom.#autoCloseListenerAdded = true;

      document.addEventListener('click', (event) => {
        const detailsToClose = [...document.querySelectorAll('details[data-auto-close-details][open]')].filter(
          (element) => {
            const closingOn = window.innerWidth < 768 ? 'mobile' : 'desktop';
            return (
              element.getAttribute('data-auto-close-details')?.includes(closingOn) &&
              !(event.target instanceof Node && element.contains(event.target))
            );
          }
        );

        for (const detailsElement of detailsToClose) detailsElement.removeAttribute('open');
      });
    }
  };

  static #autoCloseListenerAdded = false;

  /**
   * Sets the default open state from `open-by-default-on-mobile` and `open-by-default-on-desktop`.
   * Opens when the matching attribute is present (hasAttribute).
   */
  #setDefaultOpenState() {
    const isMobile = this.#isMobileViewport();
    const openOnMobile = this.hasAttribute('open-by-default-on-mobile');
    const openOnDesktop = this.hasAttribute('open-by-default-on-desktop');

    this.details.open = (isMobile && openOnMobile) || (!isMobile && openOnDesktop);
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
